'use client';

import type { FormTableColumn } from '@connected/form-fields/components/form-table';
import type { Option, OptionValue } from '@connected/form-fields/types';

import {
    BooleanSelectField,
    NumberField,
    SelectField,
    TextField,
} from '@connected/form-fields/components/form-table';

import type {
    DeviceActions,
    DeviceFieldName,
    DeviceForm,
    DeviceFormValues,
    DevicePatcher,
    SelectOption,
} from '../../distribution-board-device/types/hook-types';

import { RisoFieldCell, RowActionsCell } from '../../distribution-board-device/components/shared';

// ============================================================================
// Types
// ============================================================================

export type RCCBColumnConfig = {
    form: DeviceForm;
    createPatcher: (deviceId: string) => DevicePatcher;
    getFieldOptions: (fieldName: DeviceFieldName, deviceIndex: number) => SelectOption[];
    isFieldDisabled: (fieldName: DeviceFieldName, deviceIndex: number) => boolean;
    actions: DeviceActions;
    enableExtendedRiso: boolean;
    rowProps?: {
        electricalProjectVersionId: string;
        electricalProjectDistributionBoardId: string;
        page: number;
    };
};

// ============================================================================
// Column Factory
// ============================================================================

/**
 * Create RCCB columns based on configuration.
 * Column visibility is controlled by enableExtendedRiso flag.
 */
export function createRCCBColumns(config: RCCBColumnConfig): FormTableColumn<DeviceFormValues>[] {
    const {
        form: configForm,
        createPatcher,
        getFieldOptions,
        isFieldDisabled,
        actions,
        enableExtendedRiso,
        rowProps,
    } = config;

    const columns: FormTableColumn<DeviceFormValues>[] = [
        // Identifier (Naam)
        {
            accessorKey: 'identifier',
            header: 'Naam',
            size: '12%',
            minSize: '8ch',
            cell: ({ form, fieldName, row }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: ({ value }) => patcher.patchField('identifier')({ value: value ?? '' }),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <TextField field={field as any} placeholder="Naam" />
                        )}
                    </form.Field>
                );
            },
        },

        // ResidualCurrentDeviceType (Type)
        {
            accessorKey: 'residualCurrentDeviceType',
            header: 'Type',
            size: '8%',
            minSize: '5ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                const options = getFieldOptions('residualCurrentDeviceType', rowIndex);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('residualCurrentDeviceType'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <SelectField
                                field={field as any}
                                options={options as Option<OptionValue>[]}
                                disabled={isFieldDisabled('residualCurrentDeviceType', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // RatedCurrent (Δ Waarde (A))
        {
            accessorKey: 'ratedCurrent',
            header: 'Δ Waarde (A)',
            size: '10%',
            minSize: '9ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                const options = getFieldOptions('ratedCurrent', rowIndex);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('ratedCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <SelectField
                                field={field as any}
                                options={options as Option<OptionValue>[]}
                                disabled={isFieldDisabled('ratedCurrent', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // TrippingCharacteristic (Soort) - uses API options for RCCB
        {
            accessorKey: 'trippingCharacteristic',
            header: 'Soort',
            size: '8%',
            minSize: '4ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                const options = getFieldOptions('trippingCharacteristic', rowIndex);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('trippingCharacteristic'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <SelectField
                                field={field as any}
                                options={options as Option<OptionValue>[]}
                                disabled={isFieldDisabled('trippingCharacteristic', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // BreakerRatedCurrent (Waarde) - uses API options for RCCB
        {
            accessorKey: 'breakerRatedCurrent',
            header: 'Waarde',
            size: '8%',
            minSize: '5ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                const options = getFieldOptions('breakerRatedCurrent', rowIndex);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('breakerRatedCurrent'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <SelectField
                                field={field as any}
                                options={options as Option<OptionValue>[]}
                                disabled={isFieldDisabled('breakerRatedCurrent', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // TripDelay (Δt (ms))
        {
            accessorKey: 'tripDelay',
            header: 'Δt (ms)',
            size: '8%',
            minSize: '7ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('tripDelay'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <NumberField
                                field={field as any}
                                placeholder="ms"
                                disabled={isFieldDisabled('tripDelay', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // IsTestButtonFunctional (Testknop)
        {
            accessorKey: 'isTestButtonFunctional',
            header: 'Testknop',
            size: '15%',
            minSize: '6ch',
            cell: ({ form, fieldName, row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <form.Field
                        name={fieldName}
                        validators={{
                            onChangeAsync: patcher.patchField('isTestButtonFunctional'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <BooleanSelectField
                                field={field as any}
                                trueLabel="Ja"
                                falseLabel="Nee"
                                disabled={isFieldDisabled('isTestButtonFunctional', rowIndex)}
                            />
                        )}
                    </form.Field>
                );
            },
        },

        // RisoValue - conditional based on enableExtendedRiso
        {
            accessorKey: 'risoValue',
            header: 'Riso (MΩ)',
            size: '24%',
            minSize: '12ch',
            visible: !enableExtendedRiso,
            cell: ({ row, rowIndex }) => {
                const patcher = createPatcher(row.id);
                return (
                    <RisoFieldCell
                        form={configForm}
                        rowIndex={rowIndex}
                        patcher={patcher}
                        disabled={false}
                    />
                );
            },
        },

        // Actions column
        {
            accessorKey: 'id',
            header: '',
            size: '4%',
            minSize: '3ch',
            cell: ({ row }) => (
                <RowActionsCell
                    deviceId={row.id}
                    actions={actions}
                    rowProps={rowProps}
                />
            ),
        },
    ];

    return columns;
}
